import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";
import { hasNewBuild } from "../lib/buildVersion";
import {
  awayThresholdMs,
  checkUpdateSafety,
  installActivityTracking,
  lastActivityAt,
} from "../lib/updateSafety";
import { isAway, isScreenChange } from "../../shared/update-safety";

// 얼마나 자주 묻나 — /version.txt 는 십수 바이트짜리 no-store 파일이라 1분에 한 번 물어도
// 비용이 없다(배포 → 화면이 알아채기까지 최대 1분).
const POLL_MS = 60 * 1000;
// 화면을 옮길 때마다 묻되, 직전 확인에서 이만큼은 지나야 다시 묻는다(연타 방지).
const MIN_GAP_MS = 15 * 1000;

interface NewBuildState {
  /** 새 버전이 나왔나 — 머리 줄 「업데이트」 버튼이 이것만 본다. */
  available: boolean;
  /** 「업데이트」 — 통째로 새로고침해 새 코드를 받는다. */
  reload: () => void;
}

const NewBuildContext = createContext<NewBuildState>({
  available: false,
  reload: () => window.location.reload(),
});

/**
 * "새 버전이 나갔나"를 한 곳에서 지켜보고, **사용자가 보고 있는 화면은 저절로 새로고침하지 않는다.**
 * 규칙 진본은 워크스페이스 프론트 공통 규칙 §13 «새 판 갈아타기», 판정은 shared/update-safety.ts.
 *
 * 왜 갈아타야 하나: 화면과 서버가 같은 이미지로 함께 나가도, 배포 **전**부터 열려 있던 탭은 옛
 * 자바스크립트를 계속 쓴다. 그 탭이 새 서버의 달라진 응답을 받으면 화면 한가운데서 터진다(2026-09-03
 * 구간표 업로드). 그래서 옛 탭이 스스로 알아채고 새 코드로 갈아타야 한다.
 *
 * 언제 갈아타나(2026-11-04 사용자 결정 — «깜빡거리면 안 되니까 업데이트 버튼을 만들어 주고,
 * 화면을 이동할 때는 자동으로»):
 *  1. 1분마다 + 탭 복귀 + 화면 이동(15초 간격) 때 /version.txt 를 견줘 새 버전을 **알아채 두기만** 한다.
 *  2. 알아채면 머리 줄에 「업데이트」 버튼이 선다(NewBuildHeaderButton) — 누르면 곧바로 새 버전.
 *  3. **다른 화면(경로)으로 옮기는 순간** 잃을 게 없으면 저절로 새로고침한다 — 옮긴 화면이 그려지기 전
 *     (useLayoutEffect)에 하므로 깜빡임으로 안 보인다. 물음표 뒤(창·필터·탭)만 바뀐 건 같은 화면이라 안 한다.
 *     잃을 게 있으면(저장 중·글이 든 칸·떠 있는 창·떠나면 잃는 화면·고객 입력 화면) 이번엔 건너뛰고
 *     다음 옮길 때 다시 본다.
 *  4. 가만히 보는 화면은 그대로 둔다. 예외는 **자리 비움**(입력 없이 30분 + 잃을 것 없음) 하나 —
 *     판매점 PC 는 브라우저를 켜 둔 채 퇴근했다가 다음 날 그대로 쓴다.
 *
 * 🔴 2026-09-05 ~ 11-04 에는 «안전하면 보는 중에도 1.5초 "업데이트 중…" 뒤 새로고침, 위험하면 모달로
 *    묻기» 였다 — 손을 뗀 채 읽던 화면이 배포마다 번쩍였다. 다시 그쪽으로 돌리지 않는다.
 *
 * 새로고침은 주소를 그대로 두므로(창·탭 상태도 주소에 실린다 — 프론트 공통 규칙) 그 화면이 새 버전으로 뜬다.
 * 로컬 개발(dev 빌드)에서는 hasNewBuild 가 항상 false 라 아무 일도 없다.
 */
export function NewBuildProvider({ children }: { children: ReactNode }) {
  const [available, setAvailable] = useState(false);
  const availableRef = useRef(false);
  availableRef.current = available;
  const lastCheckRef = useRef(0);
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    installActivityTracking();
  }, []);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  // ── 1) 새 버전 알아채기 + 자리 비움 ─────────────────────────────
  const probe = useCallback(async () => {
    if (availableRef.current || document.visibilityState === "hidden") return;
    lastCheckRef.current = Date.now();
    if (await hasNewBuild()) setAvailable(true);
  }, []);

  useEffect(() => {
    let disposed = false;
    const tick = () => {
      if (disposed) return;
      // 자리 비움 — 화면이 가려진 채(모니터 꺼짐·다른 창) 켜 둔 탭도 1분마다 본다.
      if (
        availableRef.current &&
        isAway(Date.now(), lastActivityAt(), awayThresholdMs()) &&
        checkUpdateSafety(pathnameRef.current).safe
      ) {
        window.location.reload();
        return;
      }
      void probe();
    };
    const timer = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      disposed = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [probe]);

  // ── 2) 화면을 옮기는 순간 — 옮긴 화면이 그려지기 전에 갈아탄다 ──────
  const shownPathRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const previous = shownPathRef.current;
    shownPathRef.current = pathname;
    if (!isScreenChange(previous, pathname)) return;
    if (availableRef.current && checkUpdateSafety(pathname, previous).safe) {
      window.location.reload();
    }
  }, [pathname]);

  // 화면을 옮길 때 새 버전도 물어 둔다 — 배포 직후 메뉴를 누르는 사람이 1분 타이머를 기다리지 않게.
  // 여기서 알아채도 바로 새로고침하지 않는다(이미 옮긴 화면을 보기 시작했다) — 버튼을 세우고 다음 옮길 때.
  useEffect(() => {
    if (Date.now() - lastCheckRef.current < MIN_GAP_MS) return;
    void probe();
  }, [pathname, probe]);

  const value = useMemo<NewBuildState>(() => ({ available, reload }), [available, reload]);

  return <NewBuildContext.Provider value={value}>{children}</NewBuildContext.Provider>;
}

export function useNewBuild(): NewBuildState {
  return useContext(NewBuildContext);
}
